/**
 * «Cuadrar la semana»: the owner going over the week's money, a step at a time
 * (`routes/CuadrarSemana.tsx`), and when the home screen offers it.
 *
 * The owner asked, 2026-10-03, for the money side to go the way the till does: in steps that say
 * what each thing means, rather than screens that assume the person knows what an arqueo, a
 * conciliación or cuentas por pagar are. It was built as a day first; the same day the owner said
 * how the shop works: the cashiers count every shift, and the owner goes over the money once a
 * week, and the month at its end. So the week: the envelopes the tills left, what went out of the
 * safe, the safe counted, the transfers and the statements, the suppliers, and what it left.
 */

import { addMoney, centavos, sumarDias, usdToCordobas, type Centavos, type FxRate } from '@abastece/shared';
import {
  pendienteDeCompra,
  pendienteDeGasto,
  type Compra,
  type FormaPagoVenta,
  type Gasto,
  type SesionCaja,
  type Venta,
} from '@abastece/core';

/** How many days a week's going over covers, and how long until the next is due. */
export const DIAS_DE_LA_SEMANA = 7;

/**
 * The days the going over covers: those since the last one, today included. The first time, the
 * last seven days; again on the day of the last one, that day alone.
 */
export function semanaPorCuadrar(ultimo: string | null, dia: string): { desde: string; hasta: string } {
  if (ultimo === null) return { desde: sumarDias(dia, -(DIAS_DE_LA_SEMANA - 1)), hasta: dia };
  if (ultimo >= dia) return { desde: dia, hasta: dia };
  return { desde: sumarDias(ultimo, 1), hasta: dia };
}

/**
 * Whether the home screen offers «Cuadrar la semana»: a week after the last day gone over, or,
 * never gone over yet, once a till has closed and there is a week to go over.
 */
export function hayQueCuadrar(x: {
  /** The last day the week was gone over to (`semana.cuadrada`'s `hasta`). */
  ultimo: string | null;
  hoy: string;
  /** Whether any till has ever closed. */
  hayCortes: boolean;
}): boolean {
  if (x.ultimo === null) return x.hayCortes;
  return x.hoy >= sumarDias(x.ultimo, DIAS_DE_LA_SEMANA);
}

/** What the envelopes waiting add up to, each currency on its own: dollars in USD cents. */
export function totalDeSobres(sesiones: readonly Pick<SesionCaja, 'porRecibir' | 'porRecibirDolares'>[]): {
  cordobas: Centavos;
  dolares: Centavos;
} {
  return {
    cordobas: centavos(sesiones.reduce((a, s) => a + (s.porRecibir ?? 0), 0)),
    dolares: centavos(sesiones.reduce((a, s) => a + (s.porRecibirDolares ?? 0), 0)),
  };
}

/**
 * A dollar account's statement as the books carry it (`cuenta.conciliada`'s `saldo`): what they
 * carry now, and the dollars the statement has over or under theirs at the official rate. Not the
 * statement converted whole: each deposit went on converted on its own, and the parts converted do
 * not add up to the whole converted (US$ 100, 57 and 20 went on as C$ 6,482.51; US$ 177 is
 * C$ 6,482.50), nor are dollars that went in at another rate worth today's. A statement that reads
 * what the books have moves nothing.
 */
export function saldoDelEstadoEnDolares(x: {
  /** What the books carry the account at, in córdobas. */
  libros: Centavos;
  librosDolares: Centavos;
  estadoDolares: Centavos;
  tasa: FxRate;
}): Centavos {
  const diferencia = x.estadoDolares - x.librosDolares;
  const enCordobas = diferencia >= 0 ? usdToCordobas(centavos(diferencia), x.tasa) : -usdToCordobas(centavos(-diferencia), x.tasa);
  return centavos(x.libros + enCordobas);
}

/** A debt to a supplier: a purchase or an expense on credit, and what is still owed on it. */
export interface PorPagar {
  tipo: 'compra' | 'gasto';
  id: string;
  proveedorId: string | null;
  /** The invoice's number for a purchase, what it was for an expense. */
  detalle: string | null;
  fecha: string;
  /** When it has to be paid by; an expense on credit has no date. */
  vence: string | null;
  pendiente: Centavos;
}

/** Everything still owed to suppliers: the first due first, then the largest, the undated last. */
export function porPagar(compras: readonly Compra[], gastos: readonly Gasto[]): PorPagar[] {
  const deudas: PorPagar[] = [
    ...compras
      .filter((c) => pendienteDeCompra(c) > 0)
      .map((c) => ({
        tipo: 'compra' as const,
        id: c.id,
        proveedorId: c.proveedorId,
        detalle: c.documento,
        fecha: c.fecha,
        vence: c.vencePago,
        pendiente: pendienteDeCompra(c),
      })),
    ...gastos
      .filter((g) => pendienteDeGasto(g) > 0)
      .map((g) => ({
        tipo: 'gasto' as const,
        id: g.id,
        proveedorId: g.proveedorId,
        detalle: g.descripcion,
        fecha: g.fecha,
        vence: null,
        pendiente: pendienteDeGasto(g),
      })),
  ];
  return deudas.sort((a, b) => (a.vence ?? '9999').localeCompare(b.vence ?? '9999') || b.pendiente - a.pendiente);
}

/**
 * What is owed to suppliers and due by the end of the coming week, or overdue, or with no date to
 * be due by: what has to be paid, or thought about, before the next going over.
 */
export function porPagarPronto(compras: readonly Compra[], gastos: readonly Gasto[], hasta: string): PorPagar[] {
  const limite = sumarDias(hasta, DIAS_DE_LA_SEMANA);
  return porPagar(compras, gastos).filter((d) => d.vence === null || d.vence <= limite);
}

/** What the sales were paid with: córdobas applied to the bills, and the dollars counted in dollars. */
export interface Cobrado {
  porForma: Partial<Record<FormaPagoVenta, Centavos>>;
  /** USD cents taken as dollars. */
  dolares: Centavos;
  ventas: number;
}

/** Where the change comes off first: what was handed over in córdobas, then in dollars. */
const VUELTO_DE: Partial<Record<FormaPagoVenta, number>> = { efectivo_cordobas: 0, efectivo_dolares: 1 };

/**
 * How the period's sales were paid, each way on its own. A voided sale took nothing; a transfer
 * that never came took nothing either, and one that went on the client's account is fiado now.
 *
 * What was handed over less the change: a C$ 85 sale paid with a C$ 100 note took C$ 85 in cash,
 * and the change went back in córdobas, so it comes off the córdobas handed over first, then off
 * the dollars, as `efectivoDeVenta` takes it off the cash. The dollars stay as they came in.
 */
export function cobradoPorForma(ventas: readonly Venta[]): Cobrado {
  const porForma: Partial<Record<FormaPagoVenta, Centavos>> = {};
  let dolares = centavos(0);
  let n = 0;
  for (const v of ventas) {
    if (v.anulada) continue;
    n += 1;
    let vuelto: number = v.cambio;
    const pagos = v.pagos
      .filter((p) => p.estado !== 'rechazado')
      .sort((a, b) => (VUELTO_DE[a.forma] ?? 2) - (VUELTO_DE[b.forma] ?? 2));
    for (const p of pagos) {
      const menos = Math.min(vuelto, p.monto);
      vuelto -= menos;
      porForma[p.forma] = addMoney(porForma[p.forma] ?? centavos(0), centavos(p.monto - menos));
      if (p.forma === 'efectivo_dolares' && p.montoOriginal !== null) dolares = addMoney(dolares, p.montoOriginal);
    }
  }
  return { porForma, dolares, ventas: n };
}
